import React, { createContext, useContext, useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing authentication on app load
  useEffect(() => {
    const savedAuth = localStorage.getItem('glanalyzer_auth');
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth);
        if (authData.token && authData.user) {
          setIsAuthenticated(true);
          setUser(authData.user);
        } else {
          localStorage.removeItem('glanalyzer_auth');
        }
      } catch (error) {
        console.error('Error parsing saved auth data:', error);
        localStorage.removeItem('glanalyzer_auth');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.emailVerificationRequired) {
          return { success: true, emailVerificationRequired: true, signupToken: data.signupToken, user: data.user };
        }
        if (data.mfaRequired) {
          return { success: true, mfaRequired: data.mfaRequired, mfaMethods: data.mfaMethods, preAuthToken: data.preAuthToken, user: data.user };
        }
        setIsAuthenticated(true);
        setUser(data.user);
        
        // Save token and user data to localStorage
        localStorage.setItem('glanalyzer_auth', JSON.stringify({
          token: data.token,
          user: data.user,
          timestamp: Date.now()
        }));
        
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed', pendingApproval: !!data.pendingApproval, rejected: !!data.rejected };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const completePasskeyLogin = async (token, user) => {
    setIsAuthenticated(true);
    setUser(user);
    localStorage.setItem('glanalyzer_auth', JSON.stringify({
      token,
      user,
      timestamp: Date.now()
    }));
  };

  const completeEmailOtpLogin = async (token, user) => {
    setIsAuthenticated(true);
    setUser(user);
    localStorage.setItem('glanalyzer_auth', JSON.stringify({
      token,
      user,
      timestamp: Date.now()
    }));
  };

  const completeTotpLogin = async (token, user) => {
    setIsAuthenticated(true);
    setUser(user);
    localStorage.setItem('glanalyzer_auth', JSON.stringify({
      token,
      user,
      timestamp: Date.now()
    }));
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('glanalyzer_auth');
    
    // Clear all cache keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('stocksummary_') || key.startsWith('openpositions_') || key.startsWith('global_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  // Get auth token for API requests
  const getAuthToken = () => {
    const savedAuth = localStorage.getItem('glanalyzer_auth');
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth);
        return authData.token;
      } catch (error) {
        console.error('Error getting auth token:', error);
        return null;
      }
    }
    return null;
  };

  // Refresh authentication state from localStorage (called after token refresh)
  const refreshAuthState = () => {
    const savedAuth = localStorage.getItem('glanalyzer_auth');
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth);
        if (authData.token && authData.user) {
          setIsAuthenticated(true);
          setUser(authData.user);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Error refreshing auth state:', error);
        setIsAuthenticated(false);
        setUser(null);
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  // Mark the features splash as seen (persists server-side + updates local state)
  const markSplashSeen = async () => {
    const token = getAuthToken();
    if (token) {
      fetch(`${API_BASE_URL}/api/auth/splash-seen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
    }
    setUser(prev => {
      const updated = { ...prev, splashSeen: true };
      const savedAuth = localStorage.getItem('glanalyzer_auth');
      if (savedAuth) {
        try {
          const authData = JSON.parse(savedAuth);
          authData.user = updated;
          localStorage.setItem('glanalyzer_auth', JSON.stringify(authData));
        } catch (e) { /* ignore */ }
      }
      return updated;
    });
  };

  const value = {
    isAuthenticated,
    user,
    login,
    completePasskeyLogin,
    completeEmailOtpLogin,
    completeTotpLogin,
    logout,
    loading,
    getAuthToken,
    refreshAuthState,
    markSplashSeen
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;